import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';
import { credsService, UpdateCredentialsPayload, UserCredentialsResponse } from '../services/creds';

// Reusable icon components
const EyeIcon: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
  </svg>
);

const EyeOffIcon: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
  </svg>
);

interface Credentials {
  token?: string;
  organization?: string;
  bucket?: string;
  key?: string;
  secret?: string;
  workspace?: string;
  project?: string;
}

type CredentialService = 'github' | 'gitlab' | 'bitbucket' | 'azure' | 's3';

interface ModalFormValues {
  token: string;
  organization: string;
  bucket: string;
  key: string;
  secret: string;
  workspace: string;
  project: string;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  
  // Static/hardcoded data for now
  const [githubCredentials, setGithubCredentials] = useState<Credentials>({
    token: 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    organization: 'my-org-name'
  });
  
  const [gitlabCredentials, setGitlabCredentials] = useState<Credentials>({
    token: 'glpat-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    organization: 'my-gitlab-group'
  });
  
  const [s3Credentials, setS3Credentials] = useState<Credentials>({
    key: '',
    secret: '',
    bucket: ''
  });

  const [bitbucketCredentials, setBitbucketCredentials] = useState<Credentials>({
    key: '',
    secret: '',
    workspace: '',
    project: ''
  });

  const [azureCredentials, setAzureCredentials] = useState<Credentials>({
    token: '',
    organization: '',
    project: ''
  });

  // Track visibility state for each field
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({
    'github-token': false,
    'github-org': false,
    'gitlab-token': false,
    'gitlab-org': false,
    'bitbucket-key': false,
    'bitbucket-secret': false,
    'bitbucket-workspace': false,
    'bitbucket-project': false,
    'azure-token': false,
    'azure-org': false,
    'azure-project': false,
    's3-key': false,
    's3-secret': false,
    's3-bucket': false
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeService, setActiveService] = useState<CredentialService | null>(null);
  const [modalFormValues, setModalFormValues] = useState<ModalFormValues>({
    token: '',
    organization: '',
    bucket: '',
    key: '',
    secret: '',
    workspace: '',
    project: ''
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string>('');

  const toggleVisibility = (fieldKey: string): void => {
    setVisibleFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  useEffect(() => {
    const fetchCredentials = async (): Promise<void> => {
      try {
        const response: UserCredentialsResponse = await credsService.fetchUserCredentials();
        const data: UserCredentialsResponse['data'] = response.data;
        setGithubCredentials({
          token: data.github_token || '',
          organization: data.github_org || ''
        });
        setGitlabCredentials({
          token: data.gitlab_token || '',
          organization: data.gitlab_org || ''
        });
        setBitbucketCredentials({
          key: data.bitbucket_key || '',
          secret: data.bitbucket_secret || '',
          workspace: data.bitbucket_workspace || '',
          project: data.bitbucket_project || ''
        });
        setAzureCredentials({
          token: data.azure_token || '',
          organization: data.azure_org || '',
          project: data.azure_project || ''
        });
        setS3Credentials({
          key: data.access_key || '',
          secret: data.secret_key || '',
          bucket: data.s3_bucket || ''
        });
      } catch (error) {
        console.error('Unable to load credentials', error);
      }
    };

    fetchCredentials();
  }, []);

  // Function to mask sensitive values
  const maskValue = (value: string): string => {
    if (!value || value.length === 0) {
      return '••••••••';
    }
    if (value.length <= 4) {
      return '•'.repeat(value.length);
    }
    if (value.length <= 8) {
      return '•'.repeat(8);
    }
    // Show first 4 and last 4 characters, mask the rest
    const start = value.substring(0, 4);
    const end = value.substring(value.length - 4);
    const masked = '•'.repeat(Math.max(8, value.length - 8));
    return `${start}${masked}${end}`;
  };

  const openModal = (service: CredentialService): void => {
    setActiveService(service);
    setModalError('');
    switch (service) {
      case 'github':
        setModalFormValues({
          token: githubCredentials.token || '',
          organization: githubCredentials.organization || '',
          bucket: '',
          key: '',
          secret: '',
          workspace: '',
          project: ''
        });
        break;
      case 'gitlab':
        setModalFormValues({
          token: gitlabCredentials.token || '',
          organization: gitlabCredentials.organization || '',
          bucket: '',
          key: '',
          secret: '',
          workspace: '',
          project: ''
        });
        break;
      case 'bitbucket':
        setModalFormValues({
          token: '',
          organization: '',
          bucket: '',
          key: bitbucketCredentials.key || '',
          secret: bitbucketCredentials.secret || '',
          workspace: bitbucketCredentials.workspace || '',
          project: bitbucketCredentials.project || ''
        });
        break;
      case 'azure':
        setModalFormValues({
          token: azureCredentials.token || '',
          organization: azureCredentials.organization || '',
          bucket: '',
          key: '',
          secret: '',
          workspace: '',
          project: azureCredentials.project || ''
        });
        break;
      case 's3':
        setModalFormValues({
          token: '',
          organization: '',
          bucket: s3Credentials.bucket || '',
          key: s3Credentials.key || '',
          secret: s3Credentials.secret || '',
          workspace: '',
          project: ''
        });
        break;
      default:
        setModalFormValues({
          token: '',
          organization: '',
          bucket: '',
          key: '',
          secret: '',
          workspace: '',
          project: ''
        });
        break;
    }
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setActiveService(null);
    setModalFormValues({
      token: '',
      organization: '',
      bucket: '',
      key: '',
      secret: '',
      workspace: '',
      project: ''
    });
    setIsSubmitting(false);
    setModalError('');
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = event.target;
    setModalFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buildPayload = (): UpdateCredentialsPayload => {
    if (!activeService) {
      return {};
    }

    switch (activeService) {
      case 'github':
        return {
          github_token: modalFormValues.token,
          github_org: modalFormValues.organization
        };
      case 'gitlab':
        return {
          gitlab_token: modalFormValues.token,
          gitlab_org: modalFormValues.organization
        };
      case 'bitbucket':
        return {
          bitbucket_key: modalFormValues.key,
          bitbucket_secret: modalFormValues.secret,
          bitbucket_workspace: modalFormValues.workspace,
          bitbucket_project: modalFormValues.project
        };
      case 'azure':
        return {
          azure_token: modalFormValues.token,
          azure_org: modalFormValues.organization,
          azure_project: modalFormValues.project
        };
      case 's3':
        return {
          access_key: modalFormValues.key,
          secret_key: modalFormValues.secret,
          s3_bucket: modalFormValues.bucket
        };
      default:
        return {};
    }
  };

  const applyUpdatedCredentialsToState = (): void => {
    if (!activeService) {
      return;
    }

    switch (activeService) {
      case 'github':
        setGithubCredentials({
          token: modalFormValues.token,
          organization: modalFormValues.organization
        });
        break;
      case 'gitlab':
        setGitlabCredentials({
          token: modalFormValues.token,
          organization: modalFormValues.organization
        });
        break;
      case 'bitbucket':
        setBitbucketCredentials({
          key: modalFormValues.key,
          secret: modalFormValues.secret,
          workspace: modalFormValues.workspace,
          project: modalFormValues.project
        });
        break;
      case 'azure':
        setAzureCredentials({
          token: modalFormValues.token,
          organization: modalFormValues.organization,
          project: modalFormValues.project
        });
        break;
      case 's3':
        setS3Credentials({
          key: modalFormValues.key,
          secret: modalFormValues.secret,
          bucket: modalFormValues.bucket
        });
        break;
      default:
        break;
    }
  };

  const handleModalSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!activeService) {
      return;
    }

    setIsSubmitting(true);
    setModalError('');

    try {
      const payload = buildPayload();
      await credsService.updateUserCredentials(payload);
      applyUpdatedCredentialsToState();
      closeModal();
    } catch (error) {
      console.error('Failed to update credentials', error);
      setModalError('Unable to update credentials. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleUpdate = (service: CredentialService): void => {
    openModal(service);
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="back-button" onClick={() => navigate('/dashboard/migration')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
          </svg>
          Back
        </button>
        <h1>Settings</h1>
      </div>

      <div className="settings-content">
        {/* GitHub Section */}
        <div className="credentials-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" fill="currentColor"/>
              </svg>
              <h2>GitHub</h2>
            </div>
            <button 
              className="update-button"
              onClick={() => handleUpdate('github')}
            >
              Update
            </button>
          </div>
          
          <div className="credentials-fields">
            <div className="field-group">
              <label>GitHub Token</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['github-token'] 
                    ? githubCredentials.token || '' 
                    : maskValue(githubCredentials.token || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('github-token')}
                  aria-label={visibleFields['github-token'] ? 'Hide token' : 'Show token'}
                >
                  {visibleFields['github-token'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>GitHub Organization</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['github-org'] 
                    ? githubCredentials.organization 
                    : maskValue(githubCredentials.organization || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('github-org')}
                  aria-label={visibleFields['github-org'] ? 'Hide organization' : 'Show organization'}
                >
                  {visibleFields['github-org'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* GitLab Section */}
        <div className="credentials-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="32" height="32" viewBox="120 100 140 160" xmlns="http://www.w3.org/2000/svg">
                <path fill="#e24329" d="M265.26416,174.37243l-.2134-.55822-21.19899-55.30908c-.4236-1.08359-1.18542-1.99642-2.17699-2.62689-.98837-.63373-2.14749-.93253-3.32305-.87014-1.1689.06239-2.29195.48925-3.20809,1.21821-.90957.73554-1.56629,1.73047-1.87493,2.85346l-14.31327,43.80662h-57.90965l-14.31327-43.80662c-.30864-1.12299-.96536-2.11791-1.87493-2.85346-.91614-.72895-2.03911-1.15582-3.20809-1.21821-1.17548-.06239-2.33468.23641-3.32297.87014-.99166.63047-1.75348,1.5433-2.17707,2.62689l-21.19891,55.31237-.21348.55493c-6.28158,16.38521-.92929,34.90803,13.05891,45.48782.02621.01641.04922.03611.07552.05582l.18719.14119,32.29094,24.17392,15.97151,12.09024,9.71951,7.34871c2.34117,1.77316,5.57877,1.77316,7.92002,0l9.71943-7.34871,15.96822-12.09024,32.48142-24.31511c.02958-.02299.05588-.04269.08538-.06568,13.97834-10.57977,19.32735-29.09604,13.04905-45.47796Z"/>
                <path fill="#fc6d26" d="M265.26416,174.37243l-.2134-.55822c-10.5174,2.16062-20.20405,6.6099-28.49844,12.81593-.1346.0985-25.20497,19.05805-46.55171,35.19699,15.84998,11.98517,29.6477,22.40405,29.6477,22.40405l32.48142-24.31511c.02958-.02299.05588-.04269.08538-.06568,13.97834-10.57977,19.32735-29.09604,13.04905-45.47796Z"/>
                <path fill="#fca326" d="M160.34962,244.23117l15.97151,12.09024,9.71951,7.34871c2.34117,1.77316,5.57877,1.77316,7.92002,0l9.71943-7.34871,15.96822-12.09024s-13.79772-10.41888-29.6477-22.40405c-15.85327,11.98517-29.65099,22.40405-29.65099,22.40405Z"/>
                <path fill="#fc6d26" d="M143.44561,186.63014c-8.29111-6.20274-17.97446-10.65531-28.49507-12.81264l-.21348.55493c-6.28158,16.38521-.92929,34.90803,13.05891,45.48782.02621.01641.04922.03611.07552.05582l.18719.14119,32.29094,24.17392s13.79772-10.41888,29.65099-22.40405c-21.34673-16.13894-46.42031-35.09848-46.55499-35.19699Z"/>
              </svg>
              <h2>GitLab</h2>
            </div>
            <button 
              className="update-button"
              onClick={() => handleUpdate('gitlab')}
            >
              Update
            </button>
          </div>
          
          <div className="credentials-fields">
            <div className="field-group">
              <label>GitLab Token</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['gitlab-token'] 
                    ? gitlabCredentials.token || '' 
                    : maskValue(gitlabCredentials.token || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('gitlab-token')}
                  aria-label={visibleFields['gitlab-token'] ? 'Hide token' : 'Show token'}
                >
                  {visibleFields['gitlab-token'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>GitLab Organization</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['gitlab-org'] 
                    ? gitlabCredentials.organization 
                    : maskValue(gitlabCredentials.organization || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('gitlab-org')}
                  aria-label={visibleFields['gitlab-org'] ? 'Hide organization' : 'Show organization'}
                >
                  {visibleFields['gitlab-org'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bitbucket Section */}
        <div className="credentials-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.5.495.867.997.867H19.74a.99.99 0 00.99-.867l3.262-19.81a.768.768 0 00-.768-.892L.778 1.213zM14.52 15.53H9.522L8.17 8.466h7.561l-1.211 7.064z" fill="#0052CC"/>
              </svg>
              <h2>Bitbucket</h2>
            </div>
            <button 
              className="update-button"
              onClick={() => handleUpdate('bitbucket')}
            >
              Update
            </button>
          </div>
          
          <div className="credentials-fields">
            <div className="field-group">
              <label>Bitbucket Key</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['bitbucket-key'] 
                    ? bitbucketCredentials.key 
                    : maskValue(bitbucketCredentials.key || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('bitbucket-key')}
                  aria-label={visibleFields['bitbucket-key'] ? 'Hide key' : 'Show key'}
                >
                  {visibleFields['bitbucket-key'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>Bitbucket Secret</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['bitbucket-secret'] 
                    ? bitbucketCredentials.secret 
                    : maskValue(bitbucketCredentials.secret || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('bitbucket-secret')}
                  aria-label={visibleFields['bitbucket-secret'] ? 'Hide secret' : 'Show secret'}
                >
                  {visibleFields['bitbucket-secret'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>

            <div className="field-group">
              <label>Bitbucket Workspace</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['bitbucket-workspace'] 
                    ? bitbucketCredentials.workspace 
                    : maskValue(bitbucketCredentials.workspace || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('bitbucket-workspace')}
                  aria-label={visibleFields['bitbucket-workspace'] ? 'Hide workspace' : 'Show workspace'}
                >
                  {visibleFields['bitbucket-workspace'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>

            <div className="field-group">
              <label>Bitbucket Project</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['bitbucket-project'] 
                    ? bitbucketCredentials.project 
                    : maskValue(bitbucketCredentials.project || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('bitbucket-project')}
                  aria-label={visibleFields['bitbucket-project'] ? 'Hide project' : 'Show project'}
                >
                  {visibleFields['bitbucket-project'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Azure Section */}
        <div className="credentials-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" fill="#0078D4"/>
              </svg>
              <h2>Azure DevOps</h2>
            </div>
            <button 
              className="update-button"
              onClick={() => handleUpdate('azure')}
            >
              Update
            </button>
          </div>
          
          <div className="credentials-fields">
            <div className="field-group">
              <label>Azure Token</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['azure-token'] 
                    ? azureCredentials.token 
                    : maskValue(azureCredentials.token || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('azure-token')}
                  aria-label={visibleFields['azure-token'] ? 'Hide token' : 'Show token'}
                >
                  {visibleFields['azure-token'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>Azure Organization</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['azure-org'] 
                    ? azureCredentials.organization 
                    : maskValue(azureCredentials.organization || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('azure-org')}
                  aria-label={visibleFields['azure-org'] ? 'Hide organization' : 'Show organization'}
                >
                  {visibleFields['azure-org'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>

            <div className="field-group">
              <label>Azure Project</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['azure-project'] 
                    ? azureCredentials.project 
                    : maskValue(azureCredentials.project || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('azure-project')}
                  aria-label={visibleFields['azure-project'] ? 'Hide project' : 'Show project'}
                >
                  {visibleFields['azure-project'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* S3 Section */}
        <div className="credentials-section">
          <div className="section-header">
            <div className="section-title">
              <svg width="32" height="32" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" aria-label="Amazon Simple Storage Service" role="img">
                <rect width="512" height="512" rx="15%" fill="#ffffff"/>
                <path fill="#e05243" d="M260 348l-137 33V131l137 32z"/>
                <path fill="#8c3123" d="M256 349l133 32V131l-133 32v186"/>
                <g fill="#e05243">
                  <path id="s3-path-a" d="M256 64v97l58 14V93zm133 67v250l26-13V143zm-133 77v97l58-8v-82zm58 129l-58 14v97l58-29z"/>
                </g>
                <use fill="#8c3123" transform="rotate(180 256 256)" xlinkHref="#s3-path-a"/>
                <path fill="#5e1f18" d="M314 175l-58 11-58-11 58-15 58 15"/>
                <path fill="#f2b0a9" d="M314 337l-58-11-58 11 58 16 58-16"/>
              </svg>
              <h2>Amazon S3</h2>
            </div>
            <button 
              className="update-button"
              onClick={() => handleUpdate('s3')}
            >
              Update
            </button>
          </div>
          
          <div className="credentials-fields">
            <div className="field-group">
              <label>S3 Access Key</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['s3-key'] 
                    ? s3Credentials.key || '' 
                    : maskValue(s3Credentials.key || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('s3-key')}
                  aria-label={visibleFields['s3-key'] ? 'Hide access key' : 'Show access key'}
                >
                  {visibleFields['s3-key'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>S3 Secret Key</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['s3-secret'] 
                    ? s3Credentials.secret || '' 
                    : maskValue(s3Credentials.secret || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('s3-secret')}
                  aria-label={visibleFields['s3-secret'] ? 'Hide secret key' : 'Show secret key'}
                >
                  {visibleFields['s3-secret'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
            
            <div className="field-group">
              <label>S3 Bucket</label>
              <div className="masked-field-container">
                <div className="masked-field">
                  {visibleFields['s3-bucket'] 
                    ? s3Credentials.bucket 
                    : maskValue(s3Credentials.bucket || '')}
                </div>
                <button
                  type="button"
                  className="visibility-toggle"
                  onClick={() => toggleVisibility('s3-bucket')}
                  aria-label={visibleFields['s3-bucket'] ? 'Hide bucket' : 'Show bucket'}
                >
                  {visibleFields['s3-bucket'] ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && activeService && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Update {activeService === 's3' ? 'Amazon S3' : activeService === 'bitbucket' ? 'Bitbucket' : activeService === 'azure' ? 'Azure DevOps' : activeService.toUpperCase()} Credentials</h3>
              <button className="modal-close" type="button" onClick={closeModal} aria-label="Close modal">
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={handleModalSubmit}>
              {activeService === 'bitbucket' ? (
                <>
                  <div className="modal-field">
                    <label htmlFor="key-input">Bitbucket Key</label>
                    <input
                      id="key-input"
                      name="key"
                      type="text"
                      value={modalFormValues.key}
                      onChange={handleInputChange}
                      placeholder="Enter key"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="secret-input">Bitbucket Secret</label>
                    <input
                      id="secret-input"
                      name="secret"
                      type="text"
                      value={modalFormValues.secret}
                      onChange={handleInputChange}
                      placeholder="Enter secret"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="workspace-input">Bitbucket Workspace</label>
                    <input
                      id="workspace-input"
                      name="workspace"
                      type="text"
                      value={modalFormValues.workspace}
                      onChange={handleInputChange}
                      placeholder="Enter workspace"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="project-input">Bitbucket Project</label>
                    <input
                      id="project-input"
                      name="project"
                      type="text"
                      value={modalFormValues.project}
                      onChange={handleInputChange}
                      placeholder="Enter project"
                      required
                    />
                  </div>
                </>
              ) : activeService === 'azure' ? (
                <>
                  <div className="modal-field">
                    <label htmlFor="token-input">Azure Token</label>
                    <input
                      id="token-input"
                      name="token"
                      type="text"
                      value={modalFormValues.token}
                      onChange={handleInputChange}
                      placeholder="Enter token"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="organization-input">Azure Organization</label>
                    <input
                      id="organization-input"
                      name="organization"
                      type="text"
                      value={modalFormValues.organization}
                      onChange={handleInputChange}
                      placeholder="Enter organization"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="project-input">Azure Project</label>
                    <input
                      id="project-input"
                      name="project"
                      type="text"
                      value={modalFormValues.project}
                      onChange={handleInputChange}
                      placeholder="Enter project"
                      required
                    />
                  </div>
                </>
              ) : activeService === 's3' ? (
                <>
                  <div className="modal-field">
                    <label htmlFor="key-input">S3 Access Key</label>
                    <input
                      id="key-input"
                      name="key"
                      type="text"
                      value={modalFormValues.key}
                      onChange={handleInputChange}
                      placeholder="Enter access key"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="secret-input">S3 Secret Key</label>
                    <input
                      id="secret-input"
                      name="secret"
                      type="text"
                      value={modalFormValues.secret}
                      onChange={handleInputChange}
                      placeholder="Enter secret key"
                      required
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="bucket-input">S3 Bucket</label>
                    <input
                      id="bucket-input"
                      name="bucket"
                      type="text"
                      value={modalFormValues.bucket}
                      onChange={handleInputChange}
                      placeholder="Enter bucket name"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-field">
                    <label htmlFor="token-input">
                      {activeService === 'github' ? 'GitHub Token' : 'GitLab Token'}
                    </label>
                    <input
                      id="token-input"
                      name="token"
                      type="text"
                      value={modalFormValues.token}
                      onChange={handleInputChange}
                      placeholder="Enter token"
                      required
                    />
                  </div>

                  <div className="modal-field">
                    <label htmlFor="organization-input">
                      {activeService === 'github' ? 'GitHub Organization' : 'GitLab Organization'}
                    </label>
                    <input
                      id="organization-input"
                      name="organization"
                      type="text"
                      value={modalFormValues.organization}
                      onChange={handleInputChange}
                      placeholder="Enter organization"
                    />
                  </div>
                </>
              )}

              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-secondary-button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-primary-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;

